// components/GameTab.jsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay, faPause, faRedo, faDice, faTimes, faShieldAlt, faCrosshairs, faTree,
  faMagic, faMountain, faHandPaper
} from '@fortawesome/free-solid-svg-icons';

// Role icons mapping
const roleIcons = {
  Support: faShieldAlt,
  Carry: faCrosshairs,
  Jungle: faTree,
  Midlane: faMagic,
  Offlane: faMountain
};

function GameTab({
  roomId, timer, isRunning, paused, drawnChallenges, auditLog,
  roleAssignments, chatMessages, chatInput, setChatInput,
  positiveChallengesRef, negativeChallengesRef, chatBoxRef,
  isHost, isAdmin, users, penaltySettings,
  startGame, pauseTimer, resumeTimer, resetRoom, onRemoveDrawnChallenge,
  sendChatMessage, deleteChatMessage, assignRoles, givePenalty, isExpired
}) {

  // Helper for rendering challenge cards
  const renderChallengeCard = (challenge, type) => {
    const isPositive = type === 'positive';
    return (
      <div
        key={challenge.drawnId}
        className={`p-3 rounded-lg shadow-sm flex flex-col relative ${
          isExpired(challenge)
            ? 'bg-gray-300 text-gray-600 dark:bg-gray-800 dark:text-gray-400 line-through italic'
            : isPositive
              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
              : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100'
        }`}
      >
        <p className="text-sm">
          {challenge.text}
          {challenge.category && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
              ({challenge.category})
            </span>
          )}
        </p>
        {isAdmin && (
          <button
            onClick={() => onRemoveDrawnChallenge(challenge.drawnId)}
            className="absolute top-1 right-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Herausforderung entfernen"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Timer + Penalties */}
        <div className="flex flex-col gap-6 h-full">
          {/* Timer Section */}
          <div className="flex-1 bg-surface p-6 rounded-xl shadow-lg border border-border flex flex-col items-center justify-center text-center">
            <h2 className="text-xl font-bold mb-4 text-text">Aktueller Timer</h2>
            <div className="text-6xl font-extrabold text-primary mb-6">
              {Math.floor(timer / 60).toString().padStart(2, '0')}:{(timer % 60).toString().padStart(2, '0')}
            </div>
            {isHost && (
              <div className="flex flex-wrap justify-center gap-4 mb-4">
                <button
                  onClick={isRunning ? pauseTimer : startGame}
                  className={`px-6 py-3 rounded-lg font-semibold shadow-md transition-colors duration-200 ${isRunning && !paused ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                >
                  <FontAwesomeIcon icon={isRunning && !paused ? faPause : faPlay} className="mr-2" />
                  {isRunning && !paused ? 'Pause' : 'Start'}
                </button>
                {isRunning && paused && (
                  <button
                    onClick={resumeTimer}
                    className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    Fortsetzen
                  </button>
                )}
                <button
                  onClick={() => window.socket.emit('drawChallenge', { roomId })} // Use window.socket for modularity
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-md transition-colors"
                >
                  Manuell ziehen
                </button>
                <button
                  onClick={resetRoom}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg shadow-md transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faRedo} className="mr-2" />
                  Zurücksetzen
                </button>
              </div>
            )}
          </div>

          {/* Penalty Section */}
          <div className="flex-1 bg-surface p-6 rounded-xl shadow-lg border border-border overflow-y-auto max-h-72">
            <h2 className="text-xl font-bold mb-4 text-text">Strafen / Penalties</h2>
            {users.map(user => (
              <div
                key={user.id}
                className="flex items-center justify-between border-b border-border py-2"
              >
                <div className="flex flex-col">
                  <span className="font-semibold text-text">{user.name}</span>
                  <span className="text-sm text-text-light">
                    Penalty-Punkte: {user.penaltyPoints || 0}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => givePenalty(user.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Strafe vergeben"
                  >
                    <FontAwesomeIcon icon={faHandPaper} className="text-lg" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Drawn Challenges */}
        <div className="md:col-span-1 lg:col-span-1 bg-surface p-6 rounded-xl shadow-lg border border-border min-h-[60vh] flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-text">Zuletzt gezogene Herausforderungen</h2>
          {drawnChallenges.length === 0 ? (
            <p className="text-text-light italic text-center py-8">Noch keine Herausforderungen gezogen.</p>
          ) : (
            <div className="flex flex-col gap-4 flex-grow">
              {/* Positive Effects */}
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold mb-2 text-green-600 dark:text-green-400">Positive Effekte</h3>
                <div
                  ref={positiveChallengesRef}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-60 overflow-y-auto pr-2"
                >
                  {drawnChallenges.slice().reverse().filter(c => c.type === 'positive').length === 0 ? (
                    <p className="text-text-light italic col-span-full">Noch keine positiven Effekte gezogen.</p>
                  ) : (
                    drawnChallenges.slice().reverse().filter(c => c.type === 'positive').map(challenge =>
                      renderChallengeCard(challenge, 'positive')
                    )
                  )}
                </div>
              </div>

              {/* Negative Effects */}
              <div className="flex flex-col mt-4">
                <h3 className="text-lg font-semibold mb-2 text-red-600 dark:text-red-400">Negative Effekte</h3>
                <div
                  ref={negativeChallengesRef}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-60 overflow-y-auto pr-2"
                >
                  {drawnChallenges.slice().reverse().filter(c => c.type === 'negative').length === 0 ? (
                    <p className="text-text-light italic col-span-full">Noch keine negativen Effekte gezogen.</p>
                  ) : (
                    drawnChallenges.slice().reverse().filter(c => c.type === 'negative').map(challenge =>
                      renderChallengeCard(challenge, 'negative')
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Audit Log */}
        <div className="lg:col-span-1 bg-surface p-6 rounded-xl shadow-lg border border-border">
          <h2 className="text-xl font-bold mb-4 text-text">Protokoll</h2>
          <div className="max-h-60 md:max-h-80 overflow-y-auto pr-2">
            {auditLog.length === 0 ? (
              <p className="italic text-text-light text-center py-4">Noch keine Ereignisse protokolliert.</p>
            ) : (
              <ul className="text-sm space-y-1">
                {auditLog.slice().reverse().map((entry, i) => (
                  <li key={i} className="py-1 border-b border-border last:border-b-0 text-text-light">
                    {entry}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Role Assignments */}
        <div className="lg:col-span-1 bg-surface p-6 rounded-xl shadow-lg border border-border">
          <h2 className="text-xl font-bold mb-4 text-text">Rollenverteilung</h2>
          {roleAssignments.length === 0 ? (
            <p className="italic text-text-light">Noch keine Rollen vergeben.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {roleAssignments.map((r, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg shadow flex items-center gap-4"
                >
                  <FontAwesomeIcon icon={roleIcons[r.role]} className="text-2xl text-primary" />
                  <div>
                    <p className="font-semibold text-text">{r.username}</p>
                    <p className="text-sm text-text-light">{r.role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(isHost || isAdmin) && (
            <div className="mt-6 text-center">
              <button
                onClick={assignRoles}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-md transition-colors"
              >
                Rollen zufällig zuweisen
              </button>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="lg:col-span-1 bg-surface p-6 rounded-xl shadow-lg border border-border flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-text">Raum-Chat</h2>
          <div
            ref={chatBoxRef}
            className="overflow-y-auto mb-4 space-y-2 pr-1 max-h-64 min-h-64"
          >
            {chatMessages.map((msg, idx) => (
              <div
                key={msg.id || idx} // Use msg.id if available, fallback to index
                className={`
                  text-sm flex justify-between items-start
                  ${msg.type === 'penalty'
                    ? 'text-red-600 dark:text-red-400 font-semibold'
                    : 'text-text-light'}
                `}
              >
                <div>
                  <span className="font-semibold text-text">{msg.username}</span>{' '}
                  <span className="text-xs text-gray-500">[{msg.timestamp}]</span>: {msg.message}
                </div>
                {(isHost || isAdmin) && (
                  <button
                    onClick={() => deleteChatMessage(msg.id)}
                    title="Nachricht löschen"
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Nachricht eingeben..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendChatMessage(chatInput); }}
              className="flex-grow rounded-md border border-border py-2 px-3 bg-background text-text-light focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={() => sendChatMessage(chatInput)}
              className="bg-primary hover:bg-primary-dark text-white px-4 rounded-md font-semibold"
            >
              Senden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameTab;